import * as React from 'react'

import {Theme, ThemeSetting, defaultTheme, defaultThemeName, mobileThemeNameMap, themes} from './theme'

import {useLocalStorage} from '../hooks'
import {usePrefersColorScheme} from '@anatoliygatt/use-prefers-color-scheme'
import {ValidSystemTheme} from 'notability-services-common-universal'

export const clientThemeContext = React.createContext({
  themeValues: {
    theme: defaultTheme,
    name: defaultThemeName as ValidSystemTheme | 'custom',
    userSetting: defaultThemeName as ThemeSetting
  },
  setTheme: (_theme: ThemeSetting) => {},
  setCustomTheme: (_theme: Theme) => {}
})

type ThemeContextProps = {
  children: React.ReactNode
  forceTheme?: ValidSystemTheme
}

export function ClientThemeContextProvider({children, forceTheme}: ThemeContextProps) {
  const [storedUserSetting, setStoredUserSetting] = useLocalStorage<ThemeSetting>('theme-setting', 'auto')
  const [userSetting, setUserSetting] = React.useState<ThemeSetting>('auto')
  const [customTheme, setCustomTheme] = React.useState<Theme>({
    ...themes['light']
  })

  const preferredColorScheme = usePrefersColorScheme()

  let name: ValidSystemTheme | 'custom' =
    userSetting === 'custom' ? 'custom' : userSetting === 'auto' ? preferredColorScheme : userSetting

  if (forceTheme) {
    name = forceTheme
  }

  React.useEffect(() => {
    // to prevent SSR rehydration issue with manually-selected Dark mode not fully applying on first load of the gallery (as it's not the default value), need to apply theme setting in a useEffect
    setUserSetting(storedUserSetting)
  }, [storedUserSetting])

  const theme = name === 'custom' ? customTheme : themes[name]

  const value = React.useMemo(
    () => ({
      themeValues: {
        theme,
        name,
        userSetting
      },
      setTheme: (value: ThemeSetting) => {
        setUserSetting(value)
        setStoredUserSetting(value)
      },
      setCustomTheme
    }),
    [name, setStoredUserSetting, theme, userSetting]
  )

  const props = {value}

  return React.createElement(clientThemeContext.Provider, props, children)
}

export const serverThemeContext = React.createContext({
  nativeTheme: undefined as Theme | undefined,
  nativeThemeName: undefined as ValidSystemTheme | undefined
})

interface ServerThemeContextProps {
  children: React.ReactNode
  nativeTheme: ValidSystemTheme | null
}

export function ServerThemeContextProvider({children, nativeTheme}: ServerThemeContextProps) {
  const [themeName, setTheme] = React.useState<ValidSystemTheme | undefined>(
    nativeTheme !== null ? nativeTheme : undefined
  )

  React.useEffect(() => {
    const listener = ({data}: MessageEvent) => {
      const dataComponents = typeof data === 'string' ? data.split(':') : [] // Expected data format: "native_theme_enabled:{theme name}"
      const name = dataComponents.length > 1 ? dataComponents[1] : undefined
      const theme = name && mobileThemeNameMap[name]
      if (theme) {
        setTheme(theme)
        return
      }

      // Fallback to binary theme handler
      if (data === 'native_dark_theme_enabled') {
        setTheme('dark')
      } else if (data === 'native_dark_theme_disabled') {
        setTheme('light')
      }
    }

    window.addEventListener('message', listener)

    return () => {
      window.removeEventListener('message', listener)
    }
  }, [])

  const nativeThemeObj = themeName ? themes[themeName] : undefined
  const value = React.useMemo(
    () => ({
      nativeTheme: nativeThemeObj,
      nativeThemeName: themeName
    }),
    [nativeThemeObj, themeName]
  )

  return React.createElement(serverThemeContext.Provider, {value}, children)
}

export function LightThemeContextProvider(props: ThemeContextProps) {
  return ClientThemeContextProvider({
    ...props,
    forceTheme: 'light'
  })
}
